/**
 * File: apps/qbench-cp/reactapp/src/context/orderRequest.js
 *
 * Purpose: Provides the OrderRequestV2 context, which normalizes the legacy order-request
 *     store's raw payloads into an entity store plus work-in-progress ID lists for the newer
 *     order-request UI (three-column select-tests, recurrence).
 *
 * Scope: Order-request context — normalized entity access, recurrence actions, and test lookup
 *     selectors layered on top of the v1 order-request store.
 *
 * Tags: customer-portal, orders
 *
 * Overview: Watches the v1 order-request store's order, samples, assayToAdd, and tests fields
 *     and, on change, normalizes each payload via normalizr schemas into a deep-merged entities
 *     map plus a "wip" (work-in-progress) map of ID lists per entity type. Exposes selectors to
 *     read normalized entities and to filter/group tests by sample, assay, panel, or panel+assay
 *     ID — delegating the filtering logic to the pure functions in orderRequest.selectors.js.
 *     Also wires in useRecurrence for recurrence date get/set/remove/payload-building actions,
 *     and exposes normalizeAssays/normalizeTurnarounds setters for callers that fetch entities
 *     outside the watched v1 fields.
 *
 * Dependencies: shared/configs/normalizer/schemas (normalizr schema definitions);
 *     ./orderRequest.selectors (pure test-filtering/grouping logic).
 *
 * Exports: OrderRequestV2Provider — the context provider component; useOrderRequest — hook
 *     returning the provider's selectors/normalizers/actions.
 */

import {
  camelCase,
  get,
  groupBy,
  isArray,
  isEmpty,
  isObject,
  mapValues,
  mergeWith,
  omit,
} from 'lodash';
import { normalize as n } from 'normalizr';
import { useOrderRequestStore } from 'Order/store';
import React, { useEffect, useState } from 'react';
import schemas from 'shared/configs/normalizer/schemas';
import { EntityType } from 'shared/constants/entityTypes';
import useRecurrence from 'shared/hooks/misc/useRecurrence';
import { isPanelType } from 'shared/utils/misc';
import { filterTestsByPanelAssayId } from './orderRequest.selectors';

const OrderRequestV2Context = React.createContext(undefined);
OrderRequestV2Context.displayName = 'OrderRequestV2Context';

// Deep merge function to handle nested arrays and objects properly
function deepMerge(target, source) {
  return mergeWith({}, target, source, (objValue, srcValue) => {
    if (isArray(objValue) && isArray(srcValue)) {
      // For arrays, replace entirely to avoid reference issues
      return srcValue.map((item) => (isObject(item) ? { ...item } : item));
    }
    if (isObject(objValue) && isObject(srcValue)) {
      // For objects, continue deep merging
      return undefined; // Let mergeWith handle it
    }
    // For primitives, use source value
    return srcValue;
  });
}

export function addNormalizedEntities(payload, schema) {
  const normd = n(payload, schema);
  return [normd.entities, normd.result];
}

function pullEntityIdsByNames(payload = {}, names = []) {
  return names.reduce(
    (acc, name) => ({
      ...acc,
      [name]: Object.keys(payload[name] || {}).map(Number),
    }),
    {},
  );
}

function OrderRequestV2Provider({ children }) {
  const [wip, setWip] = useState({});
  const [entities, setEntities] = useState({});
  const {
    recurrence,
    checkEditing,
    getRecurrence,
    setRecurrence,
    removeRecurrence,
    createPayload: createRecurrencePayload,
  } = useRecurrence();
  const v1 = useOrderRequestStore();

  useEffect(() => {
    if (!isEmpty(recurrence)) setWip({ ...wip, recurrence });
  }, [recurrence]);

  useEffect(() => {
    if (!isEmpty(v1.order)) {
      const [newEntities, orderRequest] = addNormalizedEntities(
        v1.order,
        schemas.order,
      );
      setEntities(deepMerge(entities, newEntities));
      setWip({ ...wip, ...{ orderRequest } });
    }
  }, [v1.order]);

  useEffect(() => {
    if (!isEmpty(v1.samples)) {
      const [newEntities, samples] = addNormalizedEntities(
        v1.samples,
        schemas.samples,
      );
      // Don't add 'tests' — paginated samples would truncate wip.tests.
      const idMap = pullEntityIdsByNames(newEntities, [
        'panels',
        'assays',
        'tags',
      ]);
      setEntities(deepMerge(entities, newEntities));
      setWip({
        ...wip,
        ...idMap,
        ...{ samples },
      });
    }
  }, [v1.samples]);

  useEffect(() => {
    if (!isEmpty(v1.assayToAdd)) {
      const assayOrPanel = v1.assayToAdd;
      const isPanel = isPanelType(assayOrPanel);
      const [newEntities, entityId] = addNormalizedEntities(
        assayOrPanel,
        isPanel ? schemas.panel : schemas.assay,
      );
      const assayToAdd = isPanel
        ? {
            panelToAdd: entityId,
            panelAssays: (assayOrPanel?.panel_assays || [])
              .filter((pa) => pa.assay.active)
              .map((pa) => pa.assay_id),
          }
        : {
            assayToAdd: entityId,
          };
      setEntities(deepMerge(entities, newEntities));
      setWip({ ...wip, ...assayToAdd });
    } else {
      setWip(omit(wip, ['assayToAdd', 'panelToAdd', 'panelAssays']));
    }
  }, [v1.assayToAdd]);

  useEffect(() => {
    if (!isEmpty(v1.tests)) {
      const [newEntities, tests] = addNormalizedEntities(
        v1.tests,
        schemas.tests,
      );
      const uniqueTests = [...new Set(tests)];
      setEntities(deepMerge(entities, newEntities));
      setWip({ ...wip, ...{ tests: uniqueTests } });
    }
  }, [v1.tests]);

  /**
   * Getters
   */
  function getIds(entity) {
    const entityType = `${camelCase(entity)}s`;
    return (wip[entityType] || []).sort();
  }

  function getEntity(entity, id, defaultValue = {}, path = '') {
    const entityType = `${camelCase(entity)}s`;
    return get(entities, [entityType, id, path].filter(Boolean), defaultValue);
  }

  function getAssayOrPanelEntity(assayOrPanelId) {
    if (!assayOrPanelId || isEmpty(entities.assays)) return [];
    const assayOrPanel = getEntity(EntityType.ASSAY, assayOrPanelId);
    const isPanel = isPanelType(assayOrPanel);
    return [isPanel, assayOrPanel];
  }

  function getTestsBySampleId(sampleId) {
    if (!sampleId || isEmpty(wip.tests) || isEmpty(entities.tests)) return [];
    return Object.values(entities.tests).filter(
      (test) =>
        (test.sample === sampleId || test.sample_id === sampleId) &&
        wip.tests.includes(test.id),
    );
  }

  function getTestsByAssayId(assayId) {
    if (!assayId || isEmpty(wip.tests) || isEmpty(entities.tests)) return [];
    return Object.values(entities.tests).filter(
      (test) =>
        test.assay === assayId && !test.panel && wip.tests.includes(test.id),
    );
  }

  function getTestsByPanelId(panelId) {
    if (!panelId || isEmpty(wip.tests) || isEmpty(entities.tests)) return [];
    return Object.values(entities.tests).filter(
      (test) => test.panel === panelId && wip.tests.includes(test.id),
    );
  }

  function getTestsByPanelAssayId(panelId, assayId) {
    return filterTestsByPanelAssayId(
      entities.tests,
      wip.tests,
      panelId,
      assayId,
    );
  }

  function getTestsByAssayOrPanelId(assayOrPanelId) {
    if (!assayOrPanelId || isEmpty(wip.tests) || isEmpty(entities.tests))
      return [];
    const [isPanel, { id }] = getAssayOrPanelEntity(assayOrPanelId);
    return isPanel ? getTestsByPanelId(id) : getTestsByAssayId(id);
  }

  function groupTestsBySampleId(testArr = [], idOnly = true) {
    return idOnly
      ? mapValues(groupBy(testArr, 'sample_id'), (v) => v.map((v) => v.id))
      : groupBy(testArr, 'sample');
  }

  function groupTestsBySampleIdAssayPanelId(testArr = [], isPanel = false) {
    return mapValues(groupBy(testArr, 'sample_id'), (x) =>
      mapValues(groupBy(x, isPanel ? 'panel_id' : 'assay_id'), (y) =>
        y.map((z) => z.id),
      ),
    );
  }

  /**
   * Setters
   */
  function normalizeAssays(payload) {
    const [newEntities, assays] = addNormalizedEntities(
      payload,
      schemas.assays,
    );
    setEntities(deepMerge(entities, newEntities));
    setWip({ ...wip, ...{ assays } });
  }

  function normalizeTurnarounds(payload) {
    const [newEntities] = addNormalizedEntities(payload, schemas.turnarounds);
    setEntities(deepMerge(entities, newEntities));
  }

  return (
    <OrderRequestV2Context.Provider
      value={{
        wip,
        entities,
        selectors: {
          getIds,
          getEntity,
          getAssayOrPanelEntity,
          getTestsBySampleId,
          getTestsByAssayId,
          getTestsByPanelId,
          getTestsByPanelAssayId,
          getTestsByAssayOrPanelId,
          groupTestsBySampleId,
          groupTestsBySampleIdAssayPanelId,
          getRecurrence,
        },
        normalizers: {
          assays: normalizeAssays,
          turnarounds: normalizeTurnarounds,
        },
        actions: {
          checkEditing,
          setRecurrence,
          removeRecurrence,
          createRecurrencePayload,
        },
      }}
    >
      {children}
    </OrderRequestV2Context.Provider>
  );
}

function useOrderRequest() {
  const context = React.useContext(OrderRequestV2Context);
  return context;
}

export { OrderRequestV2Provider, useOrderRequest };
